import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useLazyUserQuery } from "../../graphql/query";
import { ServiceRequest } from "@/utils/ServiceRequest/service-request";
import { pushDataLayer } from "@/utils/data-layer-utils";
import * as jssha from "js-sha256";
import moment from "moment";
import { useUrlValidation } from "../validate-url";

export type AuthType = "Auth" | "UnAuth" | "DontCare";

export const useAuthRoute = (authState: AuthType) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [fetchUser, { data, called, loading }] = useLazyUserQuery();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  useUrlValidation();

  useEffect(() => {
    if (!called) {
      fetchUser();
    }
  }, [fetchUser, called]);

  useEffect(() => {
    if (loading || !data) return;

    if (data.user) {
      pushDataLayer({
        user_id: jssha.sha256(data.user.id),
        regist_month: moment(data.user.createdAt).format("YYYY年MM月"),
      });
    }

    switch (authState) {
      case "Auth":
        if (!data.user) {
          router.push("/login");
          return;
        }
        setIsAuthorized(true);
        break;
      case "UnAuth": {
        const { redirectUrl } = ServiceRequest(searchParams);
        if (!data.user) {
          setIsAuthorized(true);
          return;
        }

        redirectUrl.matchUrl({
          default: () => {
            router.push("/services");
          },
          service: () => {
            window.location.href = redirectUrl.getValue();
          },
        });
        break;
      }
      case "DontCare": {
        setIsAuthorized(true);
        break;
      }
    }
  }, [authState, data, loading, router, searchParams]);

  return isAuthorized;
};

export default useAuthRoute;
